import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, model, OnInit, output, signal } from '@angular/core';
import { LabelKey, Option } from '@shared/models';
import { FormsModule } from '@angular/forms';
import { SvgComponent } from '@shared/components/svg';

@Component({
  selector: 'app-combobox',
  imports: [NgClass, FormsModule, SvgComponent],
  templateUrl: './combobox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboboxComponent implements OnInit {
  readonly options = model.required<LabelKey[]>();
  readonly displayedOptions = signal<LabelKey[]>([]);
  readonly dropdownPosition = input<'LEFT' | 'CENTER' | 'RIGHT'>('LEFT');
  readonly displayAsBlock = input(false);
  readonly placeholder = input<string>('');
  readonly disabled = input(false);
  currentInputValue = signal('');
  readonly optionSelected = output<LabelKey>();

  readonly selected = model<LabelKey | null>(null);
  protected readonly isOpen = signal<boolean>(false);

  ngOnInit() {
    this.displayedOptions.set(this.options());
  }

  onClick(event: Event): void {
    if (this.disabled()) return;
    const input = event.target as HTMLInputElement;
    input.select();
    this.isOpen.update((current) => !current);
  }

  select(option: Option | null): void {
    this.selected.set(option);
    const label = option?.label ?? '';
    this.currentInputValue.set(label);
    this.optionSelected.emit(option as LabelKey);
    this.close();
  }

  public close(): void {
    this.isOpen.set(false);
  }

  onFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.select();
    this.displayedOptions.set(this.options());
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.displayedOptions.set(
      this.options().filter((option) => option.label.toLowerCase().includes(input.value.toLowerCase()))
    );
  }

  onBlur(): void {
    if (!this.currentInputValue()) {
      this.select(null);
      return;
    }
    this.select(this.selected());
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    const input = event.target as HTMLInputElement;
    const firstMatch = this.options().find((option) => option.label.toLowerCase().includes(input.value.toLowerCase()));
    if (firstMatch) this.select(firstMatch);
  }
}
